// 화면별 네임스페이스로 쪼갠 문자열 — 지금은 셸 하나
//
// 쪼개는 이유는 병렬 안전이다. 문자열을 한 파일에 몰면 화면을 만드는 스텝 여럿이 같은
// 파일을 동시에 편집하게 되고, 그것이 이 계획이 등록 계층과 슬롯으로 없애려는 바로 그
// 충돌이다. 그래서 네임스페이스 하나가 화면 하나에 대응하고, 새 화면을 만드는 스텝은
// 자기 네임스페이스 파일을 새로 만든다.
//
// **영어는 지금 비어 있다.** 값이 빈 문자열인 것이 "번역이 아직 없다"는 뜻이고, 그
// 자리는 한국어로 되돌린다. 구조만 세우고 문자열은 한국어만 채우는 것이 이 스텝의
// 몫이며, 영어를 채우는 것은 용어표가 서는 마지막 스텝이다. 되돌림이 없으면 언어를
// 바꾼 순간 화면이 통째로 비고, 그것은 번역 미완이 아니라 고장으로 보인다.
//
// 키가 빠지는 것은 되돌림으로 가려지지 않는다 — tests/unit/i18n/parity.test.ts 가 두
// 언어의 키 집합이 같은지를 검사한다. 되돌림은 값이 비어 있는 것을 견디는 장치이고,
// 그 검사는 키가 아예 없는 것을 잡는 장치라 서로 다른 실패를 막는다.
//
// **JSON 을 들일 때 attribute 를 붙인다.** 번들러를 지날 때는 없어도 되지만, 이 모듈을
// Node 가 직접 읽는 자리가 둘 생겼다 — 서버의 거절 코드 표와 브라우저 검사의 「영어가
// 채워졌는가」 물음이다. 붙여 두면 어느 경로로 읽혀도 같은 파일이 된다.

import enCommon from './en/common.json' with { type: 'json' }
import enCompose from './en/compose.json' with { type: 'json' }
import enDefense from './en/defense.json' with { type: 'json' }
import enEntry from './en/entry.json' with { type: 'json' }
import enErrors from './en/errors.json' with { type: 'json' }
import enRoster from './en/roster.json' with { type: 'json' }
import enShell from './en/shell.json' with { type: 'json' }
import enView from './en/view.json' with { type: 'json' }
import koCommon from './ko/common.json' with { type: 'json' }
import koCompose from './ko/compose.json' with { type: 'json' }
import koDefense from './ko/defense.json' with { type: 'json' }
import koEntry from './ko/entry.json' with { type: 'json' }
import koErrors from './ko/errors.json' with { type: 'json' }
import koRoster from './ko/roster.json' with { type: 'json' }
import koShell from './ko/shell.json' with { type: 'json' }
import koView from './ko/view.json' with { type: 'json' }

export const LANGS = ['ko', 'en'] as const
export type Lang = (typeof LANGS)[number]

/** 기본 언어. 연맹의 공용어가 한국어이고, 영어는 아직 채워지지 않았다. */
export const DEFAULT_LANG: Lang = 'ko'

export type ShellStrings = typeof koShell
export type EntryStrings = typeof koEntry
export type ComposeStrings = typeof koCompose
export type RosterStrings = typeof koRoster
/**
 * 화면 하나에 매이지 않은 말. **두 화면이 같은 글자를 내야 하는 자리만 여기 온다.**
 *
 * 지금 들어 있는 것은 정렬 토글 라벨 셋이다. 판정 계약이 「구성 화면과 열람 화면의 토글
 * 라벨이 같다」를 요구하는데, 라벨을 두 벌로 쓰면 반드시 어긋난다 — 어긋난 뒤에는 같은
 * 버튼이 두 화면에서 다른 것을 뜻하는 것처럼 읽힌다. **컴포넌트는 공유하지 않고 문자열만
 * 공유한다**: 두 화면의 토글은 놓이는 자리도 크기도 다르다.
 */
export type CommonStrings = typeof koCommon
/**
 * 열람 화면(S2). **정렬 토글 라벨은 여기 없다** — 그 셋은 위의 공용 네임스페이스가 지고,
 * 구성 화면과 이 화면이 같은 키를 읽는다. 두 벌로 적으면 반드시 어긋난다.
 */
export type ViewStrings = typeof koView
/**
 * 수비(T8). **화면 하나가 아니라 셋이 이 한 벌을 읽는다.** 적 rally 등록 폼(구성 defense 탭의
 * 좌열), 착탄순 시퀀스(구성 defense 탭의 우열), roster 아코디언(defense 열람)이다.
 *
 * 셋으로 가르지 않은 이유는 같은 말이 셋 다에 뜨기 때문이다. 「착탄 미상 (입력필요)」과
 * 「전 rally 불가」와 R·M 트리거 라벨이 그것이고, 세 벌로 적으면 반드시 어긋난다 —
 * 정렬 토글 라벨이 두 화면에서 갈릴 뻔한 것을 공용 네임스페이스로 막은 것과 같은 자리다.
 */
export type DefenseStrings = typeof koDefense
/**
 * 서버가 거절할 때 쓰는 문장들. **화면 하나가 아니라 서버가 소유하는 네임스페이스다.**
 *
 * 다른 네임스페이스는 화면이 자기 글자를 들고 있는데 이것만 거꾸로다 — 서버가 코드를
 * 보내고 화면이 그 코드를 문장으로 바꾼다. 그렇게 가른 이유는 **서버에 언어를 두지 않기
 * 위해서다.** 서버가 두 언어 표를 들면 「무엇이 잘못됐는가」와 「그것을 어떻게 말하는가」가
 * 한 자리에 섞이고, 이 저장소는 오디오 런타임에서 그 섞임을 이미 한 번 걷어냈다.
 *
 * **한국어 판은 서버도 읽는다.** 서버가 코드와 함께 한국어 문장을 실어 보내는데, 그 값을
 * 서버 소스에 따로 적으면 같은 문장이 두 곳에 살아 반드시 갈린다. 여기 하나만 둔다.
 */
export type ErrorStrings = typeof koErrors

/**
 * 되돌림은 네임스페이스마다 같은 규칙이라 한 번만 적는다.
 *
 * 화면이 늘어날 때마다 이 로직을 복사하면 어느 화면 하나가 되돌림 없이 서고, 그 화면은
 * 언어를 바꾼 순간 통째로 비어 **번역 미완이 아니라 고장으로** 보인다.
 *
 * **export 인 이유는 검사 하나뿐이다** (2026-08-17). 영어가 다 채워진 뒤로는 실제 카탈로그
 * 어디에도 빈 값이 없어서, 이 함수의 되돌림 갈래를 **한 번도 지나지 않게 됐다.** 안 지나는
 * 코드는 지워져도 아무도 모르고, 그 사실은 다음 언어(정본은 다섯을 목표로 적었다)를 여는
 * 사람이 화면이 통째로 빈 것을 볼 때 드러난다. 합성한 표로라도 계속 재려고 열어 둔다.
 */
export function withFallback<T extends Record<string, string>>(base: T, table: Partial<T>): T {
  const out = {} as T
  for (const key of Object.keys(base) as Array<keyof T>) {
    const value = table[key]
    out[key] = value === undefined || value === '' ? base[key] : value
  }
  return out
}

function fullyTranslated<T extends Record<string, string>>(base: T, table: Partial<T>): boolean {
  return (Object.keys(base) as Array<keyof T>).every((key) => {
    const value = table[key]
    return value !== undefined && value !== ''
  })
}

const SHELL: Record<Lang, Partial<ShellStrings>> = { ko: koShell, en: enShell }
const ENTRY: Record<Lang, Partial<EntryStrings>> = { ko: koEntry, en: enEntry }
const COMPOSE: Record<Lang, Partial<ComposeStrings>> = { ko: koCompose, en: enCompose }
const ROSTER: Record<Lang, Partial<RosterStrings>> = { ko: koRoster, en: enRoster }
const COMMON: Record<Lang, Partial<CommonStrings>> = { ko: koCommon, en: enCommon }
const VIEW: Record<Lang, Partial<ViewStrings>> = { ko: koView, en: enView }
const DEFENSE: Record<Lang, Partial<DefenseStrings>> = { ko: koDefense, en: enDefense }
const ERRORS: Record<Lang, Partial<ErrorStrings>> = { ko: koErrors, en: enErrors }

/** 그 언어의 셸 문자열 한 벌. 빈 값은 한국어로 메운다. */
export function shell(lang: Lang): ShellStrings {
  return withFallback(koShell, SHELL[lang])
}

/** 진입 화면(S1). */
export function entry(lang: Lang): EntryStrings {
  return withFallback(koEntry, ENTRY[lang])
}

/** 작전 구성 화면(S3). */
export function compose(lang: Lang): ComposeStrings {
  return withFallback(koCompose, COMPOSE[lang])
}

/** 작전 구성 화면의 roster 탭(S3). */
export function roster(lang: Lang): RosterStrings {
  return withFallback(koRoster, ROSTER[lang])
}

/** 두 화면이 함께 쓰는 말. 지금은 정렬 토글 라벨 셋이다. */
export function common(lang: Lang): CommonStrings {
  return withFallback(koCommon, COMMON[lang])
}

/** 열람 화면(S2) — 개인별 카운트다운과 신호 존. */
export function view(lang: Lang): ViewStrings {
  return withFallback(koView, VIEW[lang])
}

/** 수비(T8) — 적 rally 등록 폼 · 착탄순 시퀀스 · roster 아코디언 셋이 함께 읽는다. */
export function defense(lang: Lang): DefenseStrings {
  return withFallback(koDefense, DEFENSE[lang])
}

/**
 * 서버가 보낸 코드를 그 언어의 문장으로. **한국어 판이 이 코드들의 단일 원본이다.**
 *
 * 서버도 같은 표를 읽어 응답에 한국어 문장을 함께 싣는다 — 코드를 모르는 다른 클라이언트가
 * 붙었을 때 빈 응답을 받지 않게 하려는 것이고, 그 값을 서버 소스에 따로 적으면 같은 문장이
 * 두 곳에서 서로 다르게 낡는다.
 */
export function errors(lang: Lang): ErrorStrings {
  return withFallback(koErrors, ERRORS[lang])
}

/** 그 언어가 화면 전부를 자기 말로 낼 수 있는가. 지금은 한국어만 참이다. */
export function isTranslated(lang: Lang): boolean {
  return (
    fullyTranslated(koShell, SHELL[lang]) &&
    fullyTranslated(koEntry, ENTRY[lang]) &&
    fullyTranslated(koCompose, COMPOSE[lang]) &&
    fullyTranslated(koRoster, ROSTER[lang]) &&
    fullyTranslated(koCommon, COMMON[lang]) &&
    fullyTranslated(koView, VIEW[lang]) &&
    fullyTranslated(koDefense, DEFENSE[lang]) &&
    fullyTranslated(koErrors, ERRORS[lang])
  )
}

/**
 * 두 언어의 키가 갈리지 않았는지 검사가 훑을 목록. **새 네임스페이스를 만든 스텝이
 * 자기 줄을 여기 더한다** — 안 더하면 그 화면만 검사 밖에 남고, 그 사실을 아무도
 * 알려 주지 않는다.
 */
export const NAMESPACES = [
  { name: 'shell', ko: koShell as Record<string, string>, en: enShell as Record<string, string> },
  { name: 'entry', ko: koEntry as Record<string, string>, en: enEntry as Record<string, string> },
  {
    name: 'compose',
    ko: koCompose as Record<string, string>,
    en: enCompose as Record<string, string>,
  },
  // T4 roster 명부가 더한 줄. 안 더하면 이 화면만 검사 밖에 남고, 그 사실을 아무도
  // 알려 주지 않는다.
  {
    name: 'roster',
    ko: koRoster as Record<string, string>,
    en: enRoster as Record<string, string>,
  },
  // T5 작전 구성 attack 이 더한 줄. 화면 하나가 아니라 **두 화면이 함께 쓰는 말**이라
  // 이름이 화면 이름이 아니다 — 열람 화면을 세우는 스텝이 같은 키를 읽는다.
  {
    name: 'common',
    ko: koCommon as Record<string, string>,
    en: enCommon as Record<string, string>,
  },
  // T6 공격 열람이 더한 줄. 이 화면의 정렬 토글 라벨은 여기 없고 위의 `common` 에 있다 —
  // 판정 계약이 구성 화면과 같은 라벨을 요구하므로 문자열만 공유하고 컴포넌트는 각자 만든다.
  { name: 'view', ko: koView as Record<string, string>, en: enView as Record<string, string> },
  // T8 수비가 더한 줄. 이 한 벌을 읽는 화면이 셋이라(등록 폼·착탄순 시퀀스·roster 아코디언)
  // 이름이 화면 이름이 아니라 갈래 이름이다 — 셋으로 가르면 「착탄 미상」과 「전 rally 불가」가
  // 세 벌이 되어 반드시 어긋난다.
  {
    name: 'defense',
    ko: koDefense as Record<string, string>,
    en: enDefense as Record<string, string>,
  },
  // T1 KO/EN 배선이 더한 줄. **화면이 아니라 서버가 소유하는 네임스페이스라** 이름이 화면
  // 이름이 아니다 — 서버가 코드를 보내고 접속 층이 그 코드를 문장으로 바꾼다.
  {
    name: 'errors',
    ko: koErrors as Record<string, string>,
    en: enErrors as Record<string, string>,
  },
] as const
